import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminHeaderAction,
  AdminFormSecondaryAction,
  AdminFormFieldError,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminSupportingText,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  adminFormTwoColGridClass,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_PAGE_TITLE_CLASS,
  ADMIN_FORM_FOOTER_CLASS,
  AdminFormDropdownChevron,
} from '../shared/adminUx';
import { getConfigurationRuleTestRouteContext } from '../../../data/configurationTestSimulationConfig';
import {
  enrichRuleSummary,
  getConfigurationTestRegistryEntry,
} from '../../../data/configurationTestSimulation/registry';
import { formatAuditDisplay } from '../../../data/configurationTestSimulation/shared';
import { mergeDocumentRuleForList } from '../../../data/documentsConfig';
import { matchesUrlName, urlNameSegment } from '../../../utils/slugify';

const ToggleYesNo = ({ label, checked, onChange, offLabel = 'No', onLabel = 'Yes' }) => (
  <div className="flex items-center gap-3 flex-wrap w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer"
      />
      <div className="relative w-9 h-5 bg-lightgray rounded-full peer-checked:bg-primaryblue after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
      <span className="ml-2 text-sm text-offblack">{checked ? onLabel : offLabel}</span>
    </label>
  </div>
);

const SummaryRow = ({ label, value }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    <AdminDetailFieldValue className="py-2">{value}</AdminDetailFieldValue>
  </div>
);

const ResultRow = ({ label, value, highlight }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    <p
      className={`text-sm py-2 ${
        highlight === 'pass' ? 'text-freshgreen' : highlight === 'fail' ? 'text-alertred' : 'text-offblack'
      }`}
    >
      {value}
    </p>
  </div>
);

const ConfigurationRuleTestSimulation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const routeContext = useMemo(
    () => getConfigurationRuleTestRouteContext(location.pathname),
    [location.pathname],
  );

  const moduleKey = routeContext?.key;
  const ruleName = routeContext?.ruleName;
  const registry = useMemo(
    () => (moduleKey ? getConfigurationTestRegistryEntry(moduleKey) : null),
    [moduleKey],
  );
  const simulator = registry?.simulator;

  const ruleFromState = location.state?.rule;
  const existingRulesFromState = location.state?.existingRules;

  const allRules = useMemo(
    () => existingRulesFromState ?? (registry?.buildSampleRules ? registry.buildSampleRules() : []),
    [existingRulesFromState, registry],
  );

  const foundRule = useMemo(() => {
    if (ruleFromState?.id) {
      return allRules.find((r) => r.id === ruleFromState.id) || ruleFromState;
    }
    if (ruleName) {
      return allRules.find((r) => matchesUrlName(r.ruleName, ruleName));
    }
    return null;
  }, [ruleFromState, allRules, ruleName]);

  const returnPath =
    location.state?.returnPath || (foundRule ? `${registry?.basePath}/${urlNameSegment(foundRule.ruleName)}/edit` : registry?.basePath);

  const rule = useMemo(
    () => (registry && foundRule ? registry.getLatestSavedRule(ruleFromState, allRules, foundRule.id) : null),
    [registry, ruleFromState, allRules, foundRule],
  );

  const defaults = useMemo(
    () => (simulator ? simulator.buildDefaultTestForm(rule || {}) : {}),
    [simulator, rule],
  );

  const ruleSummary = useMemo(
    () => (simulator && rule ? enrichRuleSummary(rule, simulator) : []),
    [simulator, rule],
  );

  const [form, setForm] = useState(defaults);
  const [errors, setErrors] = useState({});
  const [runMessage, setRunMessage] = useState('');
  const [simulationResult, setSimulationResult] = useState(null);

  useEffect(() => {
    setForm(defaults);
    setErrors({});
    setRunMessage('');
    setSimulationResult(null);
  }, [defaults]);

  const handleChange = (field, value) => {
    setErrors((prev) => ({ ...prev, [field]: undefined, form: undefined }));
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const renderField = (field) => {
    if (field.type === 'toggle') {
      return (
        <ToggleYesNo
          key={field.key}
          label={field.label}
          checked={Boolean(form[field.key])}
          onChange={(checked) => handleChange(field.key, checked)}
        />
      );
    }

    if (field.type === 'select') {
      const options = simulator.resolveOptions(field, rule || {});
      return (
        <div key={field.key} className="space-y-1 w-full min-w-0">
          <AdminDetailFieldLabel>
            {field.label} {field.required && <span className="text-alertred">*</span>}
          </AdminDetailFieldLabel>
          <div className="relative w-full min-w-0">
            <select
              id={`cfg-test-${field.key}`}
              value={form[field.key] || ''}
              onChange={(e) => handleChange(field.key, e.target.value)}
              className={adminFormDetailSelectClasses(Boolean(errors[field.key]))}
            >
              <option value="">{field.placeholder || 'Select'}</option>
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <AdminFormDropdownChevron />
          </div>
          {errors[field.key] && <AdminFormFieldError>{errors[field.key]}</AdminFormFieldError>}
        </div>
      );
    }

    return (
      <div key={field.key} className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>
          {field.label} {field.required && <span className="text-alertred">*</span>}
        </AdminDetailFieldLabel>
        <input
          type="text"
          value={form[field.key] || ''}
          placeholder={field.placeholder || ''}
          onChange={(e) => handleChange(field.key, e.target.value)}
          className={adminFormDetailInputClasses(false, Boolean(errors[field.key]))}
        />
        {errors[field.key] && <AdminFormFieldError>{errors[field.key]}</AdminFormFieldError>}
      </div>
    );
  };

  const handleRunTest = () => {
    if (!simulator || !rule) return;
    const validationErrors = simulator.validateTestForm(form, rule);
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      setRunMessage('Test could not run. Review the highlighted fields and try again.');
      setSimulationResult(null);
      return;
    }
    const result = simulator.runTestSimulation(rule, form);
    setSimulationResult(result);
    setRunMessage('Test completed.');
    setErrors({});
  };

  const handleReset = () => {
    setForm(defaults);
    setErrors({});
    setRunMessage('');
    setSimulationResult(null);
  };

  const handleBackToRule = () => {
    if (!rule?.id) return;
    navigate(returnPath, {
      state: {
        rule,
        existingRules: allRules,
        testResult: simulationResult || undefined,
      },
    });
  };

  const handleSaveAndActivate = () => {
    if (!rule?.id || simulationResult?.testStatus !== 'Pass' || !registry) return;
    const nowIso = new Date().toISOString();
    const userLabel = 'Olivia Brown';

    if (registry.isDocuments) {
      const record = mergeDocumentRuleForList(rule, {
        id: rule.id,
        status: 'active',
        actor: userLabel,
        nowIso,
        isNew: false,
      });
      const merged = [...allRules.filter((r) => r.id !== rule.id), record];
      navigate('/admin/configuration/documents', { state: { rules: merged } });
      return;
    }

    const publishedBaseline = registry.getPublishedRule(allRules.find((r) => r.id === rule.id) || rule);
    let testHistory = rule.testHistory || [];
    if (registry.appendTestHistory) {
      testHistory = registry.appendTestHistory(
        { testHistory },
        `Simulation test passed: ${simulationResult.messageShown}`,
        true,
      );
    }
    const record = {
      ...rule,
      testHistory,
      modifiedOn: nowIso,
      modifiedBy: userLabel,
    };
    const merged = registry.mergeRulesAfterActivate(allRules, record, publishedBaseline || {});
    navigate(registry.basePath, { state: { rules: merged } });
  };

  const pageTitle = routeContext?.pageTitle ?? 'Rule Test & Simulation';
  const testStatusHighlight =
    simulationResult?.testStatus === 'Pass'
      ? 'pass'
      : simulationResult?.testStatus === 'Fail'
        ? 'fail'
        : undefined;

  if (!registry || !simulator) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>{pageTitle}</AdminPageTitle>
        <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
          <AdminSupportingText className="text-offblack">
            Test & Simulation is not available for this rule type.
          </AdminSupportingText>
        </DashboardMainContainer>
      </AdminLayout>
    );
  }

  if (!rule?.id) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>{pageTitle}</AdminPageTitle>
        <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
          <AdminSupportingText className="text-offblack">
            Save a draft rule first, then open Test & Simulation from the rule editor.
          </AdminSupportingText>
          <div className="mt-4">
            <AdminFormSecondaryAction type="button" onClick={() => navigate(registry.basePath)}>
              Back to Rules
            </AdminFormSecondaryAction>
          </div>
        </DashboardMainContainer>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>{pageTitle}</AdminPageTitle>

      <div className="space-y-4">
        <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
          <div className="space-y-4">
            <AdminPanelTitle className="text-offblack mb-4">Linked Rule Summary</AdminPanelTitle>
            <div className={adminFormTwoColGridClass}>
              {ruleSummary.map((row) => (
                <SummaryRow key={row.label} label={row.label} value={row.value} />
              ))}
              <SummaryRow label="Last Saved" value={formatAuditDisplay(rule.modifiedOn || rule.createdOn)} />
            </div>
          </div>
        </DashboardMainContainer>

        <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
          <div className="space-y-4">
            <AdminPanelTitle className="text-offblack mb-4">Test Setup</AdminPanelTitle>
            <div className={adminFormTwoColGridClass}>
              {simulator.setupFields.map((field) => renderField(field))}
            </div>
          </div>
        </DashboardMainContainer>

        <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
          <div className="space-y-4">
            <AdminPanelTitle className="text-offblack mb-4">Scenario Inputs</AdminPanelTitle>
            <div className={adminFormTwoColGridClass}>
              {simulator.inputFields.map((field) => renderField(field))}
            </div>
          </div>
        </DashboardMainContainer>

        <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
          <div className="space-y-4">
            <AdminPanelTitle className="text-offblack mb-4">Expected Result</AdminPanelTitle>
            <div className={adminFormTwoColGridClass}>
              {simulator.expectedFields.map((field) => renderField(field))}
            </div>
          </div>
        </DashboardMainContainer>

        {simulationResult ? (
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className="space-y-4">
              <AdminPanelTitle className="text-offblack mb-4">Simulation Result</AdminPanelTitle>
              <div className={adminFormTwoColGridClass}>
                <ResultRow label="Test Status" value={simulationResult.testStatus} highlight={testStatusHighlight} />
                <ResultRow
                  label="Expected and Actual Match"
                  value={simulationResult.expectedAndActualMatch}
                  highlight={testStatusHighlight}
                />
                <ResultRow label="Message Shown" value={simulationResult.messageShown} />
                <ResultRow label="Next Action" value={simulationResult.nextAction} />
                {(simulationResult.displayResults || []).map((row) => (
                  <ResultRow key={row.label} label={row.label} value={row.value} />
                ))}
              </div>
              {simulationResult.testStatus === 'Fail' && simulationResult.failReasons?.length ? (
                <div className="space-y-2 pt-2">
                  <AdminDetailFieldLabel>Reason to Fail</AdminDetailFieldLabel>
                  <ul className="text-sm text-alertred space-y-1 list-disc pl-5">
                    {simulationResult.failReasons.map((reason) => (
                      <li key={reason}>{reason}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </DashboardMainContainer>
        ) : null}

        {runMessage ? (
          <p className="text-sm text-offblack px-1">{runMessage}</p>
        ) : null}
      </div>

      <div className={ADMIN_FORM_FOOTER_CLASS}>
        <div className="flex flex-wrap gap-3 items-center justify-end">
          {!simulationResult ? (
            <>
              <AdminHeaderAction type="button" onClick={handleRunTest}>
                Test Rule
              </AdminHeaderAction>
              <AdminFormSecondaryAction type="button" onClick={handleReset}>
                Reset Test
              </AdminFormSecondaryAction>
            </>
          ) : simulationResult.testStatus === 'Pass' ? (
            <>
              <AdminFormSecondaryAction type="button" onClick={handleReset}>
                Reset Test
              </AdminFormSecondaryAction>
              <AdminHeaderAction type="button" onClick={handleSaveAndActivate}>
                Save
              </AdminHeaderAction>
            </>
          ) : (
            <>
              <AdminFormSecondaryAction type="button" onClick={handleReset}>
                Reset Test
              </AdminFormSecondaryAction>
              <AdminFormSecondaryAction type="button" onClick={handleBackToRule}>
                Back to Rule
              </AdminFormSecondaryAction>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default ConfigurationRuleTestSimulation;
